// src/components/Entregas/PoPicker.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Table, Input, Space, Tag, Switch, Spin, Progress, InputNumber, Button, Typography, message, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getStockOrdenesServicios } from '../../services/entregas/servicesOrdenesCompra';

const { Text } = Typography;

// Formateador de moneda (se usa en la columna "Valor Unitario")
const currency = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return 'N/A';
    return n.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 2 });
};

/**
 * Props:
 * - open: boolean
 * - mode: '1:1' | '1:N' | 'N:1'
 * - onCancel: () => void
 * - onConfirm: (rowsSeleccionadasConCantidad) => void   // opcional (si sigues usando “Usar selección”)
 * - onSave: ({ mode, planIds:number[], planTargets:any[], poRows:any[] }) => Promise<void>  // guardar en backend
 * - planSelection: Array<{ id: string, oidPlanItem: number, label?: string, unidad?: string, cantidad?: number }>
 */

export default function PoPicker({ open, mode = '1:1', onCancel, onSave, planSelection = [] }) {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [qtyMap, setQtyMap] = useState({}); // { oidStockLote: cantidadAsignar }
    const [showAdvanced, setShowAdvanced] = useState(false); // 👈 columnas avanzadas
    const [saving, setSaving] = useState(false);

    const [serialesSeleccionados, setSerialesSeleccionados] = useState([]);


    // Estado del modal de seriales
    const INITIAL_SERIAL_MODAL = { open: false, row: null, sel: [] };
    const [serialModal, setSerialModal] = useState(INITIAL_SERIAL_MODAL);

    // cargar stock
    const fetchData = async () => {
        try {
            setLoading(true);
            const resp = await getStockOrdenesServicios(); // {status, data: []}
            const arr = Array.isArray(resp?.data) ? resp.data : [];
            // solo con disponibilidad > 0
            const withStock = arr.filter((r) => Number(r.disponible_asignar || 0) > 0);
            setRows(withStock);
            setFiltered(withStock);
        } catch (e) {
            console.error('[PoPicker] Error:', e);
            message.error('No se pudo cargar el stock disponible.');
            setRows([]);
            setFiltered([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        fetchData();
        setSelectedRowKeys([]);
        setQtyMap({});
        setSearch('');
    }, [open]);

    // búsqueda
    useEffect(() => {
        const term = (search || '').toLowerCase();
        if (!term) {
            setFiltered(rows);
            return;
        }
        const next = rows.filter((r) => {
            const candidates = [
                r.productoPO,
                r.numero_orden,
                r.proveedor_nombre,
                String(r.oidPo || ''),
                String(r.oidStockLote || ''),
            ];
            return candidates.some((c) => (c || '').toLowerCase().includes(term));
        });
        setFiltered(next);
    }, [search, rows]);



    const closeSerialModal = () => setSerialModal({ open: false, row: null, sel: [] });

    const confirmSerials = () => {
        const { row, sel } = serialModal;
        setSerialesSeleccionados(prev => ({ ...prev, [row.oidPo_Producto]: sel }));
        setSerialModal(INITIAL_SERIAL_MODAL);
        closeSerialModal();
    };

    // 🧮 Falta de seriales por fila seleccionada (true = hay error)
    const missingSerials = useMemo(() => {
        // Acceso rápido por oidStockLote
        const rowByKey = new Map(filtered.map(r => [r.oidStockLote, r]));

        for (const key of selectedRowKeys) {
            const row = rowByKey.get(key);
            if (!row) continue;

            const qty = Number(qtyMap[key] || 0);
            if (qty <= 0) continue; // si no asigna cantidad, no exige seriales

            const requiereSeriales = Number(row.total_seriales || 0) > 0;
            if (!requiereSeriales) continue;

            const sel = serialesSeleccionados[row.oidPo_Producto] || [];
            if (sel.length !== qty) {
                return true; // falta escoger seriales en esta fila
            }
        }
        return false;
    }, [selectedRowKeys, qtyMap, serialesSeleccionados, filtered]);


    // selección: radio para 1:1 | N:1 (un solo PO), checkbox para 1:N (varios PO)
    const rowSelection = useMemo(() => {
        const type = mode === '1:N' ? 'checkbox' : 'radio';
        return {
            type,
            selectedRowKeys,
            onChange: (keys) => {
                setSelectedRowKeys(keys);
                // limitar qtyMap a seleccionados
                setQtyMap((prev) => {
                    const next = {};
                    keys.forEach((k) => {
                        if (prev[k] != null) next[k] = prev[k];
                    });
                    return next;
                });
            },
            // deshabilita filas sin stock
            getCheckboxProps: (record) => ({
                disabled: Number(record.disponible_asignar || 0) <= 0,
            }),
        };
    }, [selectedRowKeys, mode]);

    // columnas base + avanzadas
    const allColumns = useMemo(() => {
        // abrir modal (desde la fila)
        const handleOpenSerial = (row) => {
            const must = Number(qtyMap[row.oidStockLote] || 0);
            if (must <= 0) {
                message.warning('Primero ingresa la cantidad a asignar para esta fila.');
                return;
            }
            const pre = serialesSeleccionados[row.oidPo_Producto] || [];
            setSerialModal({ open: true, row, sel: pre });

        };

        return [
            {
                title: 'Producto (PO)',
                dataIndex: 'productoPO',
                key: 'productoPO',
                render: (txt, r) => (
                    <span>
                        <Tooltip title={txt}>
                            <strong
                                style={{
                                    display: 'block',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '600px',
                                }}
                            >
                                {txt || '—'}
                            </strong>
                        </Tooltip>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <Text type="secondary">Unidad: {r.unidad_medida ?? '—'}</Text>
                            {r.total_seriales > 0 && (
                                <Tag color="#2db7f5">Seriales: {r.total_seriales}</Tag>
                            )}
                        </div>
                    </span>
                ),
            },
            {
                title: 'Orden',
                key: 'orden',
                render: (_, r) => (
                    <span>
                        <Text>{r.numero_orden || '—'}</Text>
                        <br />
                        <Text type="secondary">
                            {r.fecha_po ? dayjs(r.fecha_po).format('DD/MM/YYYY') : '—'}
                        </Text>
                    </span>
                ),
            },
            // ——— Avanzadas (ocultas inicialmente) ———
            {
                title: 'Proveedor',
                key: 'col_proveedor',
                render: (_, r) => (
                    <span>
                        <Tag color="purple" style={{ marginBottom: 4 }}>
                            {r.proveedor_nombre || '—'}
                        </Tag>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {r.proveedor_identificacion || r.proveedor_correo || '—'}
                        </Text>
                    </span>
                ),
            },
            {
                title: 'Valor Unitario',
                dataIndex: 'valor_unitario',
                key: 'col_valor_unitario',
                align: 'right',
                render: (v) => currency(v),
            },
            // ————————————————————————————————
            {
                title: 'Disponible',
                dataIndex: 'disponible_asignar',
                key: 'disponible_asignar',
                align: 'center',
                render: (v) => {
                    const n = Number(v) || 0;
                    return (
                        <Tag color={n > 0 ? 'green' : 'red'}>{n.toLocaleString('es-CO')}</Tag>
                    );
                },
            },
            // ——— Avanzada (oculta inicialmente) ———
            {
                title: '% Asignado',
                dataIndex: 'porcentaje_asignado',
                key: 'col_porcentaje_asignado',
                align: 'center',
                render: (v) => {
                    const p = Number(v) || 0;
                    return (
                        <div style={{ minWidth: 100 }}>
                            <Progress percent={Math.max(0, Math.min(100, p))} size="small" />
                        </div>
                    );
                },
            },
            // ————————————————————————————————
            {
                title: 'Asignar cantidad',
                dataIndex: 'cantidad_asignar',
                key: 'cantidad_asignar',
                align: 'right',
                render: (_, r) => {
                    const max = Number(r.disponible_asignar || 0);
                    const value = qtyMap[r.oidStockLote] ?? null;
                    const disabled = !selectedRowKeys.includes(r.oidStockLote);
                    return (
                        <Space size={8}>
                            <InputNumber
                                min={0}
                                max={max}
                                precision={0}
                                style={{ width: 140 }}
                                disabled={disabled}
                                value={value}
                                onChange={(val) => {
                                    const v = Number(val || 0);
                                    setQtyMap((prev) => ({ ...prev, [r.oidStockLote]: v }));
                                }}
                                placeholder={disabled ? 'Seleccione primero' : `Máx: ${max}`}
                            />
                            {r.total_seriales > 0 && (
                                <Tag onClick={() => handleOpenSerial(r)} style={{ cursor: 'pointer' }}>
                                    {`Seriales: ${(serialesSeleccionados[r.oidPo_Producto]?.length || 0)
                                        }/${qtyMap[r.oidStockLote] || 0}`}
                                </Tag>
                            )}
                        </Space>
                    );
                },
            },
        ];
    }, [qtyMap, selectedRowKeys, serialesSeleccionados]);



    // filtra columnas por toggle
    const displayedColumns = useMemo(() => {
        if (showAdvanced) return allColumns;
        const hideKeys = new Set(['col_proveedor', 'col_valor_unitario', 'col_porcentaje_asignado']);
        return allColumns.filter(col => !hideKeys.has(col.key));
    }, [allColumns, showAdvanced]);

    // Validación por modo: cantidades > 0 y no superar disponible
    const canConfirm = useMemo(() => {
        if (selectedRowKeys.length === 0) return false;
        // 1:1 y N:1 => solo 1 fila PO seleccionada
        if ((mode === '1:1' || mode === 'N:1') && selectedRowKeys.length !== 1) return false;
        // 1:N => >= 1 fila PO seleccionada
        if (mode === '1:N' && selectedRowKeys.length < 1) return false;

        // cantidades válidas
        for (const key of selectedRowKeys) {
            const row = filtered.find((r) => r.oidStockLote === key);
            const max = Number(row?.disponible_asignar || 0);
            const val = Number(qtyMap[key] || 0);
            if (val <= 0 || val > max) return false;
        }


        // 🚫 No permitir confirmar si faltan seriales en alguna fila que los requiere
        if (missingSerials) return false;

        return true;
    }, [selectedRowKeys, qtyMap, filtered, mode, missingSerials]);

    // arma payload compacto
    const buildPayload = () => {
        const selectedRows = selectedRowKeys
            .map((k) => filtered.find((r) => r.oidStockLote === k))
            .filter(Boolean);
        return selectedRows.map((r) => ({
            oidStockLote: r.oidStockLote,
            oidPo_Producto: r.oidPo_Producto,
            oidPo: r.oidPo,
            numero_orden: r.numero_orden,
            productoPO: r.productoPO,
            unidad_medida: r.unidad_medida,
            disponible_asignar: Number(r.disponible_asignar || 0),
            cantidad_asignar: Number(qtyMap[r.oidStockLote] || 0), // 👈 cantidad seleccionada
            valor_unitario: Number(r.valor_unitario || 0),
            proveedor_nombre: r.proveedor_nombre || null,
            seriales: serialesSeleccionados[r.oidPo_Producto] || [],
        }));
    };



    const handleSave = async () => {
        if (!onSave) return; // por si el padre no lo pasa
        const poRows = buildPayload();

        // Asegúrate que planSelection contenga objetos con { oidPlanItem }
        const planIds = (planSelection || [])
            .map(t => (t && typeof t === 'object') ? Number(t.oidPlanItem) : null)
            .filter(v => Number.isFinite(v) && v > 0);

        if (planIds.length === 0) {
            message.warning('No hay ítems de plan válidos para asignar.');
            return;
        }

        // 🔍 Revalidación de seriales por si el botón no lo cubrió
        const invalidRows = poRows.filter(row => {
            // Busca la fila completa para saber si requiere seriales
            const full = filtered.find(x => x.oidStockLote === row.oidStockLote);
            const requiereSeriales = Number(full?.total_seriales || 0) > 0;
            if (!requiereSeriales) return false;

            const cant = Number(row.cantidad_asignar || 0);
            if (cant <= 0) return false;

            const sel = row.seriales || [];
            return sel.length !== cant; // <-- inválida si no coincide cantidad vs #seriales elegidos
        });

        if (invalidRows.length > 0) {
            // Abre el modal en la primera fila inválida para que el usuario elija los seriales
            const first = invalidRows[0];
            const full = filtered.find(x => x.oidStockLote === first.oidStockLote);
            const pre = serialesSeleccionados[full.oidPo_Producto] || [];

            setSerialModal({ open: true, row: full, sel: pre });

            message.error(
                `Faltan seriales en ${invalidRows.length} fila(s). Selecciona los seriales correspondientes antes de guardar.`
            );
            return; // ❌ Bloquea el guardado
        }


        try {
            setSaving(true);

            await onSave({ mode, planIds, planTargets: planSelection, poRows });
            // refresca stock local por si quieres ver el disponible actualizado
            await fetchData();
            onCancel?.();

        } catch (e) {
            console.error('[PoPicker] save error:', e);
            message.error('No se pudo guardar la asignación.');
        } finally {
            setSaving(false);
        }
    };




    return (
        <>
            <Modal
                title="Seleccionar productos PO"
                open={open}
                onCancel={onCancel}
                width={'95%'}
                zIndex={1000}
                destroyOnClose
                style={{ top: 20 }}             // ← aquí sí funciona
                styles={{                       // ← usa styles para mask/body/etc
                    mask: { backgroundColor: 'rgba(0,0,0,0.75)' },
                    body: { maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' }, // opcional
                }}
                footer={
                    <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <div style={{ opacity: 0.8 }}>
                            <div>
                                {mode === '1:1' && <Text>Modo 1:1 — 1 producto del plan ↔ 1 producto de PO</Text>}
                                {mode === '1:N' && <Text>Modo 1:N — 1 producto del plan ↔ N productos de PO</Text>}
                                {mode === 'N:1' && <Text>Modo N:1 — N productos del plan ↔ 1 producto de PO</Text>}
                            </div>

                        </div>
                        <Space>
                            <Button onClick={onCancel} disabled={saving}>Cancelar</Button>

                            <Button type="primary" onClick={handleSave} loading={saving} disabled={!canConfirm}>
                                Guardar asignación
                            </Button>

                        </Space>
                    </Space>
                }
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        width: '100%',
                        marginBottom: 12,
                    }}
                >
                    <Input
                        placeholder="Buscar por producto, orden o proveedor…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                        prefix={<SearchOutlined />}
                        style={{ width: 360 }}
                    />

                    <Switch
                        checked={showAdvanced}
                        onChange={setShowAdvanced}
                        checkedChildren="Ocultar"
                        unCheckedChildren="Detalles"
                    />

                    {/* Empuja este bloque a la derecha */}
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        {Array.isArray(planSelection) && planSelection.length > 0 && (
                            <>
                                <Text strong style={{ whiteSpace: 'nowrap' }}>Asignar a:</Text>

                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 6,
                                        overflowX: 'auto',
                                        maxWidth: '50vw',     // ajusta si quieres mostrar más/menos
                                        paddingBottom: 4,     // evita ocultar el borde inferior de los tags al hacer scroll
                                    }}
                                >
                                    {planSelection.map((t, i) => {
                                        const id = (t && typeof t === 'object') ? t.id : t;
                                        const label = (t && typeof t === 'object') ? t.label : String(t);
                                        const extra = (t && typeof t === 'object')
                                            ? [t.unidad, t.cantidad != null ? `x${t.cantidad}` : null].filter(Boolean).join(' · ')
                                            : null;
                                        const displayText = `${label}${extra ? ` · ${extra}` : ''}`;

                                        return (
                                            <Tooltip key={id ?? i} title={displayText}>
                                                <Tag
                                                    key={id ?? i}
                                                    color="geekblue"
                                                    style={{
                                                        maxWidth: 220,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {label}{extra ? ` · ${extra}` : ''}
                                                </Tag>
                                            </Tooltip>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <Spin spinning={saving} tip="Guardando...">
                    <Table
                        rowKey={(r) => r.oidStockLote}
                        loading={loading}
                        dataSource={filtered}
                        columns={displayedColumns}
                        rowSelection={rowSelection}
                        pagination={{ pageSize: 8 }}
                        scroll={{ x: 'max-content' }}
                    />
                </Spin>

            </Modal>
            <Modal
                open={serialModal.open}
                onCancel={closeSerialModal}
                onOk={confirmSerials}
                destroyOnClose
                zIndex={1200}
                okButtonProps={{
                    disabled:
                        !serialModal.row ||
                        serialModal.sel.length !== Number(qtyMap[serialModal.row?.oidStockLote] || 0),
                }}
                okText={`Usar ${serialModal.sel.length}`}
                title={serialModal.row
                    ? `Seriales para: "${serialModal.row.productoPO}"`
                    : 'Seriales'}
                width="60%"
                style={{ top: 30 }}
            >
                {serialModal.row && (
                    <>
                        {/* Encabezado con datos del producto */}
                        <div style={{ marginBottom: 12, lineHeight: 1.4 }}>
                            <div><strong>Orden:</strong> {serialModal.row.numero_orden} · {serialModal.row.fecha_po ? dayjs(serialModal.row.fecha_po).format('DD/MM/YYYY') : '—'}</div>
                            <div><strong>Unidad:</strong> {serialModal.row.unidad_medida ?? '—'}</div>
                            <div>
                                <strong>Debe elegir:</strong>{' '}
                                {Number(qtyMap[serialModal.row.oidStockLote] || 0)} serial(es)
                            </div>
                        </div>

                        {/* Tabla de seriales */}
                        <Table
                            size="small"
                            rowKey={(s) => s.oid}
                            dataSource={
                                Array.isArray(serialModal.row.seriales_json)
                                    ? serialModal.row.seriales_json
                                    : JSON.parse(serialModal.row.seriales_json || '[]')
                            }
                            columns={[
                                { title: 'ID', dataIndex: 'oid', width: 90 },
                                { title: 'Serial', dataIndex: 'serial' },
                                { title: 'Nota', dataIndex: 'nota' },
                            ]}
                            pagination={false}
                            rowSelection={{
                                type: 'checkbox',
                                selectedRowKeys: serialModal.sel,
                                onChange: (keys) => {
                                    const max = Number(qtyMap[serialModal.row.oidStockLote] || 0);
                                    if (keys.length <= max) {
                                        setSerialModal((s) => ({ ...s, sel: keys }));
                                    } else {
                                        message.warning(`Solo puedes elegir ${max} serial(es) para este producto.`);
                                    }
                                },
                                // Opcional: deshabilita seriales ya usados en otras filas
                                // getCheckboxProps: (rec) => ({ disabled: false })
                            }}
                        />
                    </>
                )}
            </Modal>

        </>
    );
}
